import categories from '@data/cats.json';
import { useRouter } from 'next/router';
import { useTranslations } from 'next-intl';
import styled from 'styled-components';
import CategoriesGrid from '@/components/categories-grid';
import { Button, Section, SectionHeader } from '@/components/ui';

const COUNT = 6;

/* Most to least wanted: the first six that exist and are not excluded are shown. */
const PREFERRED = [
  'entertainment',
  'travel',
  'house-and-home',
  'groceries',
  'gaming',
  'clothing-and-accessories',
  'restaurants-and-food',
  'coffee',
  'sports',
  'hotels',
  'flights',
  'wine-and-cocktails',
  'health-and-spa',
  'golf',
  'renovations',
  'maternity-and-toys',
  'gifts-for-her',
  'gifts-for-him',
];

// Only what the grid renders, so the rest of the category record stays out of the markup.
const bySlug = new Map(categories.map(({ id, slug }) => [slug, { id, slug }]));

/**
 * The categories to show, skipping the ones the page already shows (`exclude`: slugs).
 * `current` is the category the page is about: it is never suggested, and the list
 * starts with the one after it, so each category page offers a different six.
 */
export const pickCategories = (exclude = [], current) => {
  const start = PREFERRED.indexOf(current) + 1;
  const rotated = [...PREFERRED.slice(start), ...PREFERRED.slice(0, start)];

  return rotated
    .filter((slug) => bySlug.has(slug) && slug !== current && !exclude.includes(slug))
    .slice(0, COUNT)
    .map((slug) => bySlug.get(slug));
};

/** The category a page is about: `/shop/{category}` and `/buy/{country}/{category}`. */
export const currentCategory = ({ pathname, query }) => {
  if (pathname === '/shop/[slug]') return query.slug;
  if (pathname === '/buy/[[...slug]]') return query.slug?.[1];

  return undefined;
};

const Grid = styled(CategoriesGrid)`
  margin-top: 40px;
`;

const Actions = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 32px;
`;

/**
 * "Keep browsing" band for the bottom of catalogue pages: six category photo
 * cards (one row from 1024px) and a link to the full catalogue.
 *
 *   <BrowseMore />
 *   <BrowseMore exclude={[category.slug]} />
 *
 * `exclude`: slugs the page already shows (the categories in a grid higher
 * up), so the band offers something else. On a category page the page's own
 * category is left out and the suggestions rotate with it.
 */
const BrowseMore = ({ exclude }) => {
  const t = useTranslations('browseMore');
  const router = useRouter();

  return (
    <Section id="browse-more" $background="color-mix(in oklab, var(--muted) 50%, transparent)">
      <SectionHeader align="center" title={t('title')} />
      <Grid categories={pickCategories(exclude, currentCategory(router))} columns={COUNT} />
      <Actions>
        <Button $size="pill" href="/buy">
          {t('browseAll')}
        </Button>
      </Actions>
    </Section>
  );
};

export default BrowseMore;
